import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/lib/api";

axios.defaults.withCredentials = true;

let refreshing = null;
axios.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err.config;
    const url = cfg?.url || "";
    if (err.response?.status === 401 && !cfg._retry && !url.includes("/auth/")) {
      cfg._retry = true;
      refreshing = refreshing || axios.post(`${API}/auth/refresh`).finally(() => (refreshing = null));
      try {
        await refreshing;
        return axios(cfg);
      } catch (e) {
        window.dispatchEvent(new Event("auth-expired"));
      }
    }
    return Promise.reject(err);
  }
);

export const formatErr = (e) => {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  return d.msg || String(d);
};

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);

  const loadMe = useCallback(
    () =>
      axios.get(`${API}/auth/me`).then((r) => setUser(r.data)).catch(async () => {
        try {
          const r = await axios.post(`${API}/auth/refresh`);
          setUser(r.data);
        } catch {
          setUser(false);
        }
      }),
    []
  );

  useEffect(() => {
    loadMe();
    const onExpired = () => setUser(false);
    window.addEventListener("auth-expired", onExpired);
    return () => window.removeEventListener("auth-expired", onExpired);
  }, [loadMe]);

  const login = useCallback(async (email, password) => setUser((await axios.post(`${API}/auth/login`, { email, password })).data), []);
  const register = useCallback(async (email, password, name) =>
    setUser((await axios.post(`${API}/auth/register`, { email, password, name })).data), []);
  const logout = useCallback(async () => {
    await axios.post(`${API}/auth/logout`).catch(() => {});
    setUser(false);
  }, []);

  const value = useMemo(() => ({ user, login, register, logout, reload: loadMe }), [user, login, register, logout, loadMe]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
